import { Disclosure } from "@headlessui/react";
import classNames from "classnames";
import Image from "next/image";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { CSSProperties, useEffect, useRef, useState } from "react";

import CrossCountry from "~/types/cross-country";
import Constants from "~/utils/constants";
import imageUtils from "~/utils/image-utils";
import intlUtils from "~/utils/intl-utils";
import productUtils from "~/utils/product-utils";
import TrackingUtils from "~/utils/tracking-utils";

import Icon from "../common/icon";
import IconChevronDown from "../common/icons/icon-chevron-down";
import IconChevronUp from "../common/icons/icon-chevron-up";
import styles from "./accordion-shade-selector.module.scss";
import { ShadeSelectorWrapper } from "./product-shade-selector";

type Props = {
  root: CrossCountry.DetailedProduct | CrossCountry.Product;
  selectedShade: CrossCountry.ChildrenProduct;
  shades: CrossCountry.Product[];
  shadesOOS?: Record<string, boolean>;
  hideAccordion?: boolean;
  version: "v1" | "v2";
  onAccordionChange?: (isOpen: boolean) => void;
};

export default function AccordionShadeSelector(props: Props) {
  const t = useTranslations();
  const priceFormatter = intlUtils.usePriceFormatter();
  const goccia = productUtils.getProductDroplet(props.selectedShade?.product_media?.media);
  const { shade_name, shade_number, color } = props.selectedShade;
  const [shadesExpanded, setShadesExpanded] = useState(false);
  const [styleMaxHeight, setStyleMaxHeight] = useState<CSSProperties>({});

  // the width of the label that will be used to create a transparent label is kept track of
  const refLabel = useRef<HTMLSpanElement>(null);
  const [widthLabel, setWidthLabel] = useState<number | undefined>();

  const styleLabelLine = {
    "--label-line-width": `calc(100% - ${widthLabel}px - var(--label-external-padding-horizontal))`,
  } as CSSProperties;

  useEffect(() => {
    setWidthLabel(refLabel.current?.offsetWidth);
  }, []);

  //accordion height management
  const accordionRef = useRef<HTMLDivElement>(null);

  /**
   * Update the accordion max-height CSS variable when the panel opens.
   *
   * When the accordion content would extend past the viewport or the enclosing
   * PDP_GALLERY_SECTION boundary, we constrain the panel height so the shade list stays
   * scrollable instead of overflowing the page.
   *
   * The computed height is applied via a CSS custom property that is consumed
   * by the .shadesSelector rule. When the accordion closes or the DOM nodes are
   * unavailable, the property is cleared to restore the default behavior.
   */
  const updateAccordionMaxHeight = (isOpen: boolean) => {
    if (!isOpen) {
      setStyleMaxHeight({});
      return;
    }

    const accordionRect = accordionRef.current?.getBoundingClientRect();
    const gallerySectionRect = document.getElementById(Constants.PDP_GALLERY_SECTION)?.getBoundingClientRect();

    if (!accordionRect || !gallerySectionRect) {
      setStyleMaxHeight({});
      return;
    }

    const innerHeight = window.innerHeight;
    const marginSafety = 22; // buffer for padding, borders, and potential overflow
    const marginBottomAccordion = 32; // extra bottom spacing for the accordion panel

    let maxHeight = 0;

    if (accordionRect.bottom >= innerHeight && gallerySectionRect.bottom > innerHeight) {
      // Content is already extending beyond the viewport bottom.
      maxHeight = innerHeight - accordionRect.top - marginSafety - marginBottomAccordion;
    } else if (accordionRect.bottom >= gallerySectionRect.bottom) {
      // Content is extending past the section boundary.
      const realBottom = gallerySectionRect.height + Constants.HEADER_HEIGHT_DESKTOP - window.scrollY;
      maxHeight = realBottom - accordionRect.top - marginSafety;
    }

    setStyleMaxHeight(maxHeight > 0 ? ({ "--max-height-shades-selector": `${maxHeight}px` } as CSSProperties) : {});
  };

  useEffect(() => {
    if (shadesExpanded && props.version === "v2") {
      updateAccordionMaxHeight(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.root.product_id]);

  return (
    <Disclosure
      as="div"
      className={classNames(styles.accordion, props.hideAccordion ? styles.hideAccordion : undefined)}
    >
      <Disclosure.Button
        className={styles.accordionButton}
        onClick={() => {
          if (props.version === "v2") {
            updateAccordionMaxHeight(!shadesExpanded);
          }
          props.onAccordionChange?.(!shadesExpanded);
          setShadesExpanded(!shadesExpanded);
        }}
      >
        <div className={styles.wrapperShadesCount}>
          <span className={styles.shadesCount} ref={refLabel} style={styleLabelLine}>
            {t("generic.shadescount", { count: props.shades?.length })}
          </span>
        </div>
        <div className={styles.shadesHeader}>
          <div className={styles.shadesInfo}>
            {goccia ? (
              <div className={styles.shadeImageWrapper}>
                {props.shadesOOS?.[props.selectedShade.product_id] && <span className={styles.outOfStock} />}
                <Image
                  className={styles.shadeImage}
                  alt={props.selectedShade.display_value}
                  src={goccia.url}
                  height={28}
                  width={28}
                  loader={imageUtils.evaImageLoader}
                />
              </div>
            ) : null}
            <div>
              {shade_number && color ? (
                <>
                  <p className={styles.shadeColorBold}>
                    {shade_number} {shade_name ?? ""}
                  </p>
                  <p className={styles.shadeColor}>{color}</p>
                </>
              ) : (
                <p className={styles.shadeColorBold}>{color}</p>
              )}
            </div>
          </div>
          <Icon icon={shadesExpanded ? IconChevronUp : IconChevronDown} aria-hidden width={16} height={16} />
        </div>
      </Disclosure.Button>
      <Disclosure.Panel static className={styles.panel}>
        <div className={styles.panelInner}>
          {props.version === "v1" ? (
            <ShadeSelectorWrapper
              root={props.root}
              selectedShade={props.selectedShade}
              shades={props.shades}
              shadesOOS={props.shadesOOS}
              open={shadesExpanded}
            />
          ) : (
            <div className={props.version === "v2" ? styles.panelInnerV2 : undefined}>
              <div ref={accordionRef} style={styleMaxHeight} className={styles.shadesSelector}>
                {props.shades?.map((shade) => {
                  return (
                    <Link
                      key={shade.product_id}
                      role="radio"
                      scroll={false}
                      shallow
                      aria-label={shade.display_value}
                      aria-checked={shade.product_id === props.selectedShade?.product_id}
                      aria-disabled={props.shadesOOS?.[shade.product_id]}
                      href={`/p/${shade.slug ?? props.root.slug}-${shade.product_id}`}
                      className={
                        shade.product_id === props.selectedShade?.product_id
                          ? styles.selectedShadeSelectorButton
                          : styles.shadeSelectorButton
                      }
                      onClick={() => {
                        TrackingUtils.track(
                          TrackingUtils.mapEcommerceEvent("view_item", [TrackingUtils.mapEcomItem(props.root, shade)], {
                            user_action: "detail_change_color",
                          })
                        );
                      }}
                    >
                      <div className={styles.containerImageAndText}>
                        <div className={styles.shadeImageWrapper}>
                          {props.shadesOOS?.[shade.product_id] && <span className={styles.outOfStock} />}
                          <Image
                            className={styles.shadeImage}
                            alt={shade.display_value}
                            src={
                              productUtils.getProductDroplet(shade?.product_media?.media)?.url ||
                              imageUtils.transparentPixel
                            }
                            height={28}
                            width={28}
                            loader={imageUtils.evaImageLoader}
                          />
                        </div>
                        <div>
                          {shade.shade_number && shade.color ? (
                            <>
                              <p className={styles.shadeColorRegularInner}>
                                {shade.shade_number} {shade.shade_name ?? ""}
                              </p>
                              <p className={styles.shadeColorInner}>{shade.color}</p>
                            </>
                          ) : (
                            <p className={styles.shadeColorRegularInner}>{shade.color}</p>
                          )}
                        </div>
                      </div>

                      <p className={styles.price}>
                        {priceFormatter.currency(shade.display_price, { currency: shade.currency_id })}
                      </p>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </Disclosure.Panel>
    </Disclosure>
  );
}
